<template>
    <div class="scan-header">
        <div class="scan-title-container">
            <p class="scan-title">Recent Activity</p>
            <!-- <p class="scan-subtitle">Next Scan in 12 Hours</p> -->
        </div>
        <prime-button 
            v-if="company?.scanner === ScannerStatus.ACTIVE"
            class="btn-add" 
            @click="handleStartScan"
            :loading="isScanning"
            :disabled="!canStartScan"
        > 
            Start Manual Scan 
        </prime-button>
    </div>

    <div v-if="!exposureStore.selectedExposure" class="skeleton-container">
        <div class="skeleton-header">
            <div class="skeleton-title"></div>
            <div class="skeleton-subtitle"></div>
        </div>
        <div class="skeleton-content">
            <div class="skeleton-item"></div>
            <div class="skeleton-item"></div>
            <div class="skeleton-item"></div>
        </div>
    </div>

    <div v-else-if="pending" class="loading-container">
        <div class="loading-spinner"></div>
        <span>Loading activity...</span>
    </div>

    <div v-else-if="conversation.length > 0">
        <ActivityFeed :activities="conversation" :loading="pending" />
    </div>

    <div v-else class="no-activity">
        <p class="text-gray-500 text-center py-8">No Activity Found</p>
    </div>

    <prime-button label="View All Activity" @click="redirectToActivity" variant="secondary" class="w-full my-6 p-btn-root_secondary" />
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';
import { computed, watch } from 'vue';
import ActivityFeed from '~/components/shared/ActivityFeed.vue';
import { exposuresService } from '~/services/exposures-service';
import { relationshipService } from '~/services/relationship-service';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import { usePersonStore } from '~/stores/person';
import type { ConversationMessage } from '~/types/companies/conversations';
import { ModalNames } from '~/types/modals';
import { ScannerStatus } from '~/types/person/companies';

const dialogRef = getDialogRef(ModalNames.EXPOSURE_SIDEBAR);
const router = useRouter();
const personStore = usePersonStore();
const exposureStore = useExposureSidebarStore();
const { showSuccess, showError, showWarning } = useGlobalToast();
const { company } = storeToRefs(exposureStore)

const activities = ref([]);
const pending = ref(true);
const isScanning = ref(false);

const conversation = ref<ConversationMessage[]>([]);

// Check if we can start a scan - need both person and company
const canStartScan = computed(() => {
    return Boolean(personStore.person && company.value && !isScanning.value);
});

// Fetch activity data using the new exposure history endpoint
const fetchActivityData = async (): Promise<void> => {
    if (!personStore.person || !exposureStore.selectedExposure) {
        activities.value = [];
        pending.value = false;
        return;
    }

    try {
        pending.value = true;
        // Fetch activity feed data using the new exposure history endpoint
        conversation.value = await exposuresService.getExposureHistory(
            personStore.person.id,
            exposureStore.selectedExposure
        );
        // Reverse the array to show newest items first
        conversation.value = conversation.value.reverse();
    } catch (error: any) {
        showError('Error', error?.message || 'Failed to fetch activity data');
        conversation.value = [];
    } finally {
        pending.value = false;
    }
};

const handleStartScan = async () => {
    if (!personStore.person || !company.value) {
        showError('Error', 'Missing required information to start scan');
        return;
    }

    try {
        isScanning.value = true;
        await relationshipService.scanCompany(personStore.person.id, company.value.id);
        
        showSuccess('Success', 'Manual scan request successfully sent');
        
        // Optionally refresh the activity data to show the new scan
        await fetchActivityData();
    } catch (error: any) {
        showError('Error', error.message || 'Failed to start manual scan');
    } finally {
        isScanning.value = false;
    }
};

const redirectToActivity = () => {
    if (company.value?.slug) {
        router.push(`/companies/${company.value.slug}/activity`);
        dialogRef.value.close();
    }
}

fetchActivityData();

// Watch for changes in selected exposure to refetch data
watch(() => exposureStore.selectedExposure, (newExposureId) => {
    if (newExposureId) {
        fetchActivityData();
    }
});
</script>

<style lang="scss" scoped>
.scan-header {
    @apply flex w-full flex-row items-start gap-5 my-6 justify-between;
}

.scan-title-container {
    @apply flex flex-col justify-center items-start gap-0.5 flex-1 self-stretch;
}

.scan-title {
    @apply text-gray-900 font-inter text-lg font-semibold leading-7;
}

.scan-subtitle {
    @apply text-gray-600 font-inter text-sm font-normal leading-5;
}

.btn-add {
    @apply w-40 flex py-2 px-3 text-white justify-center items-center gap-1 border border-gray-200 bg-blue-600 rounded shadow-sm;
}

.loading-container {
    @apply flex flex-col items-center justify-center py-12 gap-4;

    .loading-spinner {
        @apply w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin;
    }

    span {
        @apply text-gray-600 font-inter text-sm;
    }
}

.no-activity {
    @apply text-center py-8;
}

.skeleton-container {
    @apply my-6;

    .skeleton-header {
        @apply flex flex-row items-start gap-5 justify-between mb-6;

        .skeleton-title {
            @apply h-7 bg-gray-200 rounded animate-pulse;
            width: 120px;
        }

        .skeleton-subtitle {
            @apply h-5 bg-gray-200 rounded animate-pulse;
            width: 100px;
        }
    }

    .skeleton-content {
        @apply space-y-4;

        .skeleton-item {
            @apply h-16 bg-gray-200 rounded animate-pulse;
            width: 100%;
        }
    }
}
</style>
